import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, ExternalLink, Landmark, QrCode } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export function PaymentOptions({ member, payment, onNotified }) {
  const { t } = useApp();
  const [notified, setNotified] = useState(member?.payment_notified);
  const [busy, setBusy] = useState(false);

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied"); };

  const notify = async () => {
    setBusy(true);
    try { await api.post("/membership/notify-payment"); setNotified(true); toast.success(t("notified")); onNotified?.(); }
    catch { toast.error("Please try again"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="payment-options">
      <div className="cu-card p-6 text-center">
        <div className="eyebrow">{t("annual_membership")}</div>
        <div className="font-serif-cu text-5xl gold-text mt-3">€120</div>
        <div className="mt-5 gold-hairline" />
        <div className="mt-5">
          <div className="eyebrow mb-2">{t("your_reference")}</div>
          <button onClick={() => copy(member?.payment_reference || "")} className="inline-flex items-center gap-2 font-mono-cu text-lg tracking-[0.15em] gold-text" data-testid="payment-reference">
            {member?.payment_reference || "—"} <Copy size={14} className="text-[color:var(--cu-muted)]" />
          </button>
        </div>
      </div>

      {payment?.sumup_link ? (
        <a href={payment.sumup_link} target="_blank" rel="noopener noreferrer" data-testid="pay-sumup-button" className="btn-gold w-full py-4 text-sm flex items-center justify-center gap-2">
          {t("pay_with_sumup")} <ExternalLink size={16} />
        </a>
      ) : (
        <div className="cu-card p-4 text-center text-xs text-[color:var(--cu-muted)]">{t("pay_with_sumup")} — link coming soon.</div>
      )}

      {payment?.sumup_qr_path && (
        <div className="cu-card p-6 text-center">
          <div className="eyebrow flex items-center justify-center gap-2 mb-4"><QrCode size={14} /> {t("scan_to_pay")}</div>
          <div className="bg-white rounded-xl p-3 inline-block">
            <img src={fileUrl(payment.sumup_qr_path)} alt="SumUp QR" className="w-44 h-44 object-contain" data-testid="pay-qr-code-image" />
          </div>
        </div>
      )}

      {(payment?.bank_iban || payment?.bank_account_name) && (
        <div className="cu-card p-6">
          <div className="eyebrow flex items-center gap-2 mb-4"><Landmark size={14} /> {t("bank_transfer")}</div>
          <div className="space-y-3 text-sm">
            {payment.bank_account_name && <Row label={t("account_name")} value={payment.bank_account_name} />}
            {payment.bank_iban && <Row label={t("iban")} value={payment.bank_iban} onCopy={() => copy(payment.bank_iban)} />}
            {payment.bank_bic && <Row label={t("bic")} value={payment.bank_bic} />}
            {payment.bank_note && <p className="text-xs text-[color:var(--cu-muted)] pt-2">{payment.bank_note}</p>}
          </div>
        </div>
      )}

      <button onClick={notify} disabled={busy || notified} data-testid="notify-payment-button" className="btn-ghost w-full py-3.5 text-sm">
        {notified ? t("notified") : t("notify_paid")}
      </button>
    </div>
  );
}

function Row({ label, value, onCopy }) {
  return (
    <div className="flex justify-between items-center gap-3 border-b border-[color:var(--cu-border)] last:border-0 pb-2.5 last:pb-0">
      <span className="eyebrow">{label}</span>
      <button onClick={onCopy} className={`font-mono-cu text-[color:var(--cu-text)] text-right ${onCopy ? "inline-flex items-center gap-2" : ""}`}>
        {value} {onCopy && <Copy size={12} className="text-[color:var(--cu-muted)]" />}
      </button>
    </div>
  );
}
